/**
 * EconomicIndicatorsWidget
 * =========================
 * Displays live Australian economic data from ABS, RBA, and ATO.
 * Makes Corvana a true business intelligence hub.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';
import { useAuth } from '@/contexts/AuthContext';
import { logIfReal } from '@/utils/transientError';
import {
  TrendingUp, TrendingDown, DollarSign, Users, ShoppingCart,
  Building2, RefreshCw, ExternalLink, ArrowUpRight, ArrowDownRight,
  Percent, Globe, BarChart3, Activity
} from 'lucide-react';

// Iter-316 — cookie-only auth. The `token` prop is kept for backwards
// compatibility (callers still pass it) but is intentionally unused.
const EconomicIndicatorsWidget = ({ industry = 'technology', className = '' }) => {
  const { isAuthenticated } = useAuth();
  const [indicators, setIndicators] = useState(null);
  const [atoBenchmarks, setAtoBenchmarks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    // Defer until auth is ready so we don't fire (and fail) before the
    // session cookie is established on first paint.
    if (!isAuthenticated) return;
    fetchData();
    // eslint-disable-next-line
  }, [industry, isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch economic indicators — cookie carries auth.
      const indicatorsRes = await authFetch(`${API_URL}/api/benchmarks/live/economic-indicators`);
      const indicatorsText = await indicatorsRes.text();
      const indicatorsData = JSON.parse(indicatorsText);

      if (indicatorsData.success) {
        setIndicators(indicatorsData.indicators);
        setLastUpdated(indicatorsData.last_updated);
      }

      // Fetch ATO benchmarks for user's industry.
      const atoRes = await authFetch(`${API_URL}/api/benchmarks/live/ato?industry=${industry}`);
      const atoText = await atoRes.text();
      const atoData = JSON.parse(atoText);

      if (atoData.success) {
        setAtoBenchmarks(atoData);
      }
    } catch (err) {
      logIfReal('Error fetching economic data', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getIndicatorIcon = (category) => {
    const icons = {
      inflation: Percent,
      employment: Users,
      forex: Globe,
      interest_rates: DollarSign,
      retail: ShoppingCart,
      default: BarChart3
    };
    return icons[category] || icons.default;
  };

  const getIndicatorColor = (name) => {
    if (name.includes('CPI') || name.includes('Inflation')) return 'text-amber-500';
    if (name.includes('Labour') || name.includes('Employment')) return 'text-blue-500';
    if (name.includes('Exchange') || name.includes('USD')) return 'text-green-500';
    if (name.includes('Cash Rate')) return 'text-purple-500';
    return 'text-slate-500';
  };

  const formatValue = (value, name) => {
    if (typeof value !== 'number') return value;
    if (name.includes('Exchange')) return value.toFixed(4);
    if (name.includes('CPI')) return `${value.toFixed(1)}%`;
    if (name.includes('Rate')) return `${value.toFixed(2)}%`;
    return value.toLocaleString();
  };

  if (loading) {
    return (
      <Card className={`${className}`} data-testid="economic-indicators-widget">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Loading economic data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`${className}`} data-testid="economic-indicators-widget">
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            <p>Unable to load economic data</p>
            <Button variant="ghost" size="sm" onClick={() => fetchData()} className="mt-2">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`overflow-hidden ${className}`} data-testid="economic-indicators-widget">
      <CardHeader className="pb-2 bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Activity className="w-4 h-4 text-indigo-500" />
            </div>
            <div>
              <CardTitle className="text-base">Australian Economic Pulse</CardTitle>
              <p className="text-xs text-muted-foreground">Live data from ABS, RBA, ATO</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData} className="h-8 w-8 p-0">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="p-4 space-y-4">
        {/* Key Economic Indicators */}
        {indicators && indicators.length > 0 && (
 <div className="grid grid-cols-3 gap-2">
            {indicators.map((indicator, idx) => {
              const Icon = getIndicatorIcon(indicator.category);
              const colorClass = getIndicatorColor(indicator.name);
              
              return (
                <div 
                  key={`${indicator?.id ?? indicator?.name ?? indicator?.title ?? 'indicator'}-${idx}`}
                  className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2.5 text-center"
                >
                  <div className="flex items-center justify-center mb-1">
                    <Icon className={`w-4 h-4 ${colorClass}`} />
                  </div>
                  <p className={`text-lg font-bold ${colorClass}`}>
                    {formatValue(indicator.value, indicator.name)}
                  </p>
                  <p className="text-[10px] text-muted-foreground line-clamp-1">
                    {indicator.name.replace('Consumer Price Index', 'CPI').replace('AUD/', '')}
                  </p>
                  <Badge variant="outline" className="mt-1 text-[9px] px-1 py-0">
                    {indicator.source}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}

        {/* Industry Benchmarks */}
        {atoBenchmarks && atoBenchmarks.benchmarks && (
          <div className="pt-3 border-t">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                Your Industry Benchmarks
              </h4>
              <Badge variant="secondary" className="text-[10px]">
                {industry.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Badge>
            </div>
            
 <div className="grid grid-cols-2 gap-2">
              {Object.entries(atoBenchmarks.benchmarks).slice(0, 1).map(([ind, benchmarks]) => (
                Object.entries(benchmarks).slice(0, 4).map(([key, range], idx) => (
                  <div 
                    key={`${ind}-${key}`}
                    className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 rounded-md px-2.5 py-1.5"
                  >
                    <span className="text-xs text-muted-foreground capitalize">
                      {key.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs font-medium text-primary">
                      {range.low}-{range.high}%
                    </span>
                  </div>
                ))
              ))}
            </div>
            
            <p className="text-[10px] text-muted-foreground text-center mt-2">
              ATO Small Business Benchmarks FY{atoBenchmarks.financial_year}
            </p>
          </div>
        )}

        {/* Footer with last updated */}
        <div className="flex items-center justify-between pt-2 border-t text-[10px] text-muted-foreground">
          <span>
            Updated: {lastUpdated ? new Date(lastUpdated).toLocaleString('en-AU', {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
            }) : 'Recently'}
          </span>
          <a 
            href="https://www.abs.gov.au" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-primary"
          >
            <span>ABS Data</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export default EconomicIndicatorsWidget;
